import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { auth as authApi } from '../utils/api';
import type { User } from '@/types';

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  login: (username: string, password: string) => Promise<{ user: User }>;
  logout: () => Promise<void>;
  refreshAuth: () => Promise<User | null>;
  isAdmin: boolean;
  isBauleiter: boolean;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const checkingRef = useRef(false);

  useEffect(() => {
    if (checkingRef.current || initialized) {
      return;
    }

    const publicPaths = ['/order/', '/sign/'];
    const isPublicPath = publicPaths.some(path => location.pathname.startsWith(path));

    if (isPublicPath) {
      setLoading(false);
      setInitialized(true);
      return;
    }

    const checkAuth = async () => {
      checkingRef.current = true;
      try {
        const response = await authApi.me();
        setUser(response.data);
      } catch {
        setUser(null);
      } finally {
        setLoading(false);
        setInitialized(true);
        checkingRef.current = false;
      }
    };

    checkAuth();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const login = useCallback(async (username: string, password: string) => {
    const response = await authApi.login(username, password);
    setUser(response.data.user);

    if (response.data.user.role === 'admin') {
      navigate('/admin');
    } else if (response.data.user.role === 'bauleiter') {
      navigate('/bauleiter');
    } else {
      navigate('/');
    }

    return response.data;
  }, [navigate]);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch {
      // Ignore logout errors
    } finally {
      setUser(null);
      navigate('/');
    }
  }, [navigate]);

  const refreshAuth = useCallback(async () => {
    try {
      const response = await authApi.me();
      setUser(response.data);
      return response.data;
    } catch {
      setUser(null);
      return null;
    }
  }, []);

  const isAdmin = user?.role === 'admin';
  const isBauleiter = user?.role === 'bauleiter';
  const isAuthenticated = !!user;

  const value: AuthContextValue = {
    user,
    loading,
    login,
    logout,
    refreshAuth,
    isAdmin,
    isBauleiter,
    isAuthenticated,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
